<template>
  <div class="stories-view">
    <div v-if="loading && stories.length === 0" class="text-center pa-8">
      <v-progress-circular indeterminate color="primary" />
      <div class="mt-2">Загрузка сюжетов…</div>
    </div>

    <div v-else-if="stories.length === 0" class="text-center pa-8">
      <v-icon size="64" color="grey-lighten-1">mdi-shape-outline</v-icon>
      <div class="mt-2 text-grey">Сюжетов пока нет</div>
      <div class="text-caption text-medium-emphasis mt-1">
        Сюжеты появляются по мере поступления сообщений (группировка событий).
      </div>
    </div>

    <v-expansion-panels v-else v-model="expanded" multiple variant="accordion">
      <v-expansion-panel
        v-for="story in stories"
        :key="story.story_id"
        :value="story.story_id"
      >
        <v-expansion-panel-title>
          <div class="d-flex align-center ga-2" style="width: 100%">
            <v-icon size="small" color="teal">mdi-shape</v-icon>
            <span class="font-weight-medium" v-html="titleHtml(story)"></span>
            <v-chip size="x-small" color="teal" variant="tonal">{{ story.finding_count || 0 }}</v-chip>
            <v-spacer />
            <span class="text-caption text-medium-emphasis">
              {{ formatDate(story.last_seen_at) }}
            </span>
          </div>
        </v-expansion-panel-title>

        <v-expansion-panel-text>
          <div v-if="findingsMap[story.story_id] === undefined" class="text-center pa-4">
            <v-progress-circular indeterminate size="20" color="primary" />
          </div>
          <div v-else-if="findingsMap[story.story_id].length === 0" class="text-caption text-grey pa-2">
            Нет сообщений
          </div>
          <div v-else>
            <div
              v-for="f in findingsMap[story.story_id]"
              :key="f._id"
              class="story-message py-2 px-1"
              :class="{ 'story-message--first': f._id === firstFindingMap[story.story_id] }"
              @click="$emit('open-message', f)"
            >
              <div class="d-flex align-center ga-2 mb-1">
                <v-tooltip
                  v-if="f._id === firstFindingMap[story.story_id]"
                  text="Первоисточник — раньше всех опубликовал"
                  location="top"
                >
                  <template #activator="{ props }">
                    <span v-bind="props" class="first-badge">1</span>
                  </template>
                </v-tooltip>
                <SimpleIcon v-if="isTelegramPlatform(f.platform_id)" name="telegram" :size="16" color="#9E9E9E" />
                <v-icon v-else :icon="getPlatformIcon(f.platform_id)" size="16" class="text-medium-emphasis" />
                <span class="text-caption text-medium-emphasis">{{ formatDate(f.published_at) }}</span>
                <span class="text-caption font-weight-medium text-truncate">{{ f.source_name || f.source_url }}</span>
              </div>
              <div class="text-body-2 story-snippet" v-html="snippetHtml(f)"></div>
            </div>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import { markersToText } from '@/utils/messageMarkers';
import { stripInlineMarkup } from '@/utils/text';
import apiDataClient from '@/apiDataClient';
import apiClient from '@/apiClient';
import SimpleIcon from '@/components/SimpleIcon.vue';

export default {
  name: 'MessageStoriesView',
  components: { SimpleIcon },
  props: {
    projectId: { type: [Number, String], default: null },
    // Активные фильтры панели (платформа, даты, источники/теги, сущность и т.д.).
    // Те же ключи, что уходят в /findings — прокидываются и в /stories, и в
    // findings?story_id=..., чтобы группировка учитывала всю панель фильтров.
    filterParams: { type: Object, default: () => ({}) },
    // the «Подсветка» switch of the filter panel: the words of the query marked in story titles
    highlight: { type: Boolean, default: true },
  },
  emits: ['open-message', 'total'],
  data() {
    return {
      stories: [],
      loading: false,
      expanded: [],
      findingsMap: {},   // story_id -> findings[] (undefined = not loaded yet)
      firstFindingMap: {},  // story_id -> _id of the earliest-PUBLISHED finding (primary source)
      platforms: {},     // platform_id -> {icon, name} for source-type icons
    };
  },
  watch: {
    projectId() { this.reload(); },
    filterParams: { deep: true, handler() { this.reload(); } },
    expanded(now) {
      // Lazy-load findings for any newly opened story.
      for (const storyId of now) {
        if (this.findingsMap[storyId] === undefined) {
          this.loadStoryFindings(storyId);
        }
      }
    },
  },
  mounted() {
    this.loadPlatforms();
    this.reload();
  },
  methods: {
    async loadPlatforms() {
      try {
        const r = await apiClient.get('/ref/platforms');
        const map = {};
        for (const p of (r.data.items || r.data || [])) map[p.id] = p;
        this.platforms = map;
      } catch (e) { /* icons fall back to mdi-web */ }
    },
    getPlatformIcon(platformId) {
      return this.platforms[platformId]?.icon || 'mdi-web';
    },
    isTelegramPlatform(platformId) {
      const p = this.platforms[platformId];
      return p?.icon === 'mdi-telegram' || p?.name?.toLowerCase() === 'telegram';
    },
    async reload() {
      this.stories = [];
      this.findingsMap = {};
      this.firstFindingMap = {};
      this.expanded = [];
      if (!this.projectId) return;
      this.loading = true;
      try {
        const r = await apiDataClient.get(`/projects/${this.projectId}/stories`, {
          params: { offset: 0, limit: 100, ...this.filterParams },
        });
        this.stories = r.data.items || [];
        this.$emit('total', r.data.total || 0);
      } catch (e) {
        this.stories = [];
        this.$emit('total', 0);
      } finally {
        this.loading = false;
      }
    },
    async loadStoryFindings(storyId) {
      this.findingsMap = { ...this.findingsMap, [storyId]: undefined };
      try {
        const r = await apiDataClient.get(`/projects/${this.projectId}/findings`, {
          // with the highlight: the message window opened from a story shows the found words, as
          // the flat list does (the preview below drops the marks)
          params: { ...this.filterParams, story_id: storyId, offset: 0, limit: 100, highlight: true },
        });
        const items = (r.data.items || []).slice();
        // chronological (oldest first) — the story unfolds over time
        items.sort((a, b) => new Date(a.published_at) - new Date(b.published_at));
        this.findingsMap = { ...this.findingsMap, [storyId]: items };
        this.firstFindingMap = { ...this.firstFindingMap, [storyId]: this.earliestPublishedId(items) };
      } catch (e) {
        this.findingsMap = { ...this.findingsMap, [storyId]: [] };
      }
    },
    earliestPublishedId(items) {
      // Первоисточник: находка с минимальной ВАЛИДНОЙ датой публикации
      // (не дата сбора). Помечаем только если в сюжете больше одной находки.
      if (!items || items.length <= 1) return null;
      let bestId = null;
      let bestTs = null;
      for (const it of items) {
        const ts = new Date(it.published_at).getTime();
        if (isNaN(ts)) continue;
        if (bestTs === null || ts < bestTs) { bestTs = ts; bestId = it._id; }
      }
      return bestId;
    },
    // The title of a story as HTML: escaped text, the found words (<em> of data) as <mark>
    titleHtml(story) {
      const source = this.highlight && story.highlighted_title ? story.highlighted_title : (story.title || '');
      return this.markedHtml(source) || 'Без заголовка';
    },
    // The preview of a message of a story: the found words marked, as in the flat list (the
    // findings come with highlighted_content; the plain text cut them off, 26.10.2026)
    snippetHtml(f) {
      const marked = this.highlight && f.highlighted_content;
      return this.markedHtml(marked ? f.highlighted_content : (f.content || f.title || ''), 240);
    },
    // Escaped text with the found words (<em> of data) as <mark>; maxLength cuts the visible text
    // by whole marks, so a cut never falls inside one
    markedHtml(source, maxLength = Infinity) {
      const escape = (v) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const parts = this.plainText((source || '').replace(/<em>/g, '\uE000').replace(/<\/em>/g, '\uE001'))
        .split(/\uE000([^\uE001]*)\uE001/);
      let html = '';
      let shown = 0;
      for (let i = 0; i < parts.length; i++) {
        const room = maxLength - shown;
        const part = parts[i];
        if (part.length > room) {
          if (i % 2 === 0) html += escape(part.slice(0, room));
          return html + '…';
        }
        html += i % 2 ? `<mark class="search-highlight">${escape(part)}</mark>` : escape(part);
        shown += part.length;
      }
      return html;
    },
    // Text without service markers and formatting: previews and story titles (a title made
    // from the first message of a story without the LLM may carry {{LINK:..}} or **bold**)
    plainText(value) {
      let t = value || '';
      // Strip service markers (same as the flat view's formatMessageSnippet).
      t = markersToText(t);   // the whole list of service markers, utils/messageMarkers
      t = t.replace(/<\/?em>/g, '');
      t = stripInlineMarkup(t);
      return t.replace(/\s+/g, ' ').trim();
    },
    formatDate(v) {
      if (!v) return '';
      const d = new Date(v);
      if (isNaN(d) || d.getFullYear() < 1971) return '';
      const p = (n) => String(n).padStart(2, '0');
      return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
    },
  },
};
</script>

<style scoped>
/* the found words in story titles: the look of the highlight of the messages (Messages.vue) */
:deep(.search-highlight) {
  background-color: #ffeb3b;
  color: #000;
  padding: 0.1rem 0.2rem;
  border-radius: 2px;
  font-weight: 500;
}
.story-message {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  cursor: pointer;
}
.story-message:last-child { border-bottom: none; }
.story-message:hover { background: rgba(0, 0, 0, 0.03); }

/* Первоисточник — раньше всех опубликовал (по дате публикации). */
.story-message--first {
  border: 1px solid rgba(76, 175, 80, 0.55);
  border-radius: 6px;
  background: rgba(76, 175, 80, 0.07);
}
.story-message--first:hover { background: rgba(76, 175, 80, 0.12); }
.first-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #4caf50;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  flex: 0 0 auto;
}
.story-snippet {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
